import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Eraser, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { ukDateTime } from "./crmPipelineShared";
import { LoadError, errMsg } from "./LoadError";

function EraseButton({ r, onDone }) {
  const erase = async () => {
    const typed = window.prompt(`Permanently erase personal data for "${r.name}"?\nName, contact details and notes will be removed for good. Anonymous business totals are kept.\n\nType ERASE to confirm.`);
    if (typed === null) return;
    try { await api.post(`/crm/records/${r.store}/${r.id}/erase`, { confirm: typed }); toast.success("Personal data erased"); onDone(); }
    catch (e) { toast.error(errMsg(e, "Erase failed")); }
  };
  return <button onClick={erase} data-testid={`archive-erase-${r.id}`} className="flex items-center gap-1 text-xs text-red-300 hover:text-red-200 px-2 py-1"><Eraser className="w-3.5 h-3.5" /> Erase</button>;
}

export default function CrmArchive() {
  const [state, setState] = useState("archived");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false); setData(null);
    try { const { data } = await api.get("/crm/archive", { params: { state } }); setData(data); }
    catch (e) { setError(true); }
  }, [state]);
  useEffect(() => { load(); }, [load]);

  const restore = async (r) => {
    try { const { data } = await api.post(`/crm/records/${r.store}/${r.id}/restore`); toast.success(`Restored ${data.records.length || 1} record(s)`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not restore")); }
  };

  return (
    <CrmShell title="Archive & Deleted">
      <div className="flex gap-2 mb-4">
        {[["archived", "Archived"], ["deleted", "Deleted"]].map(([k, l]) => (
          <button key={k} onClick={() => setState(k)} data-testid={`archive-tab-${k}`} className={`text-sm rounded-full px-4 py-1.5 ${state === k ? "bg-brass text-emerald-950 font-semibold" : "bg-white/5 text-emerald-100/70 hover:bg-white/10"}`}>{l}</button>
        ))}
      </div>
      <p className="text-xs text-emerald-100/50 mb-4">{state === "archived" ? "Archived records are hidden from active lists and follow-ups but keep their notes and history." : "Deleted records can be restored. Permanent erasure removes personal data for data-protection requests; anonymous totals stay in reporting."}</p>
      {error && <LoadError message="Could not load records." onRetry={load} testId="archive-error" />}
      {!error && !data && <p className="text-sm text-emerald-100/50">Loading…</p>}
      {data && (
        <div className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-x-auto" data-testid="archive-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase text-emerald-100/40 border-b border-white/10"><th className="py-2.5 px-3">Name</th><th className="px-3">Type</th><th className="px-3 hidden md:table-cell">Reason</th><th className="px-3 hidden md:table-cell">When</th><th className="px-3" /></tr></thead>
            <tbody>
              {data.records.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-emerald-100/40" data-testid="archive-empty">Nothing here.</td></tr>}
              {data.records.map((r) => (
                <tr key={`${r.store}-${r.id}`} className="border-b border-white/5" data-testid={`archive-row-${r.id}`}>
                  <td className="py-2.5 px-3"><div className="text-white">{r.name}</div><div className="text-[11px] text-emerald-100/40">{r.email || r.mobile || "—"}</div></td>
                  <td className="px-3 text-emerald-100/70 text-xs">{r.kind}{r.erased && <span className="ml-1.5 text-red-300">· erased</span>}</td>
                  <td className="px-3 hidden md:table-cell text-emerald-100/60 text-xs">{r.reason || "—"}</td>
                  <td className="px-3 hidden md:table-cell text-emerald-100/50 text-xs">{ukDateTime(r.at)}</td>
                  <td className="px-3"><div className="flex justify-end gap-1">
                    {!r.erased && <Link to={`/crm/contact/${r.store}/${r.id}`} data-testid={`archive-view-${r.id}`} className="flex items-center gap-1 text-xs text-emerald-100/70 hover:text-white px-2 py-1"><Eye className="w-3.5 h-3.5" /> View</Link>}
                    {!r.erased && <button onClick={() => restore(r)} data-testid={`archive-restore-${r.id}`} className="flex items-center gap-1 text-xs text-brass hover:underline px-2 py-1"><RotateCcw className="w-3.5 h-3.5" /> Restore</button>}
                    {state === "deleted" && !r.erased && <EraseButton r={r} onDone={load} />}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CrmShell>
  );
}
